import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface BlogArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  alt: string;
  slug: string;
}

interface BlogArticleCardProps {
  article: BlogArticle;
}

const BlogArticleCard = ({ article }: BlogArticleCardProps) => {
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'dive-reports': return 'Dive Reports';
      case 'marine-life': return 'Marine Life';
      case 'conservation': return 'Conservation';
      case 'conditions': return 'Conditions';
      case 'community': return 'Community';
      default: return category;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <article
      className="rounded-xl overflow-hidden transition-all duration-200 group"
      style={{
        background: '#0F2234',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.30)',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.border = '1px solid rgba(111,211,224,0.25)';
        el.style.boxShadow = '0 8px 30px rgba(0,0,0,0.45)';
        el.style.transform = 'translateY(-3px)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.border = '1px solid rgba(255,255,255,0.08)';
        el.style.boxShadow = '0 4px 20px rgba(0,0,0,0.30)';
        el.style.transform = 'translateY(0)';
      }}
    >
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={article.image}
          alt={article.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 left-3">
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={{
              background: 'rgba(111,211,224,0.15)',
              color: '#6FD3E0',
              border: '1px solid rgba(111,211,224,0.25)',
            }}
          >
            {getCategoryLabel(article.category)}
          </span>
        </div>
      </div>

      <div className="p-5 space-y-3">
        <h3
          className="text-xl font-headline font-bold line-clamp-2"
          style={{ color: '#EAF4F8' }}
        >
          {article.title}
        </h3>

        <p className="text-sm line-clamp-3" style={{ color: '#C8DDE6' }}>
          {article.excerpt}
        </p>

        <div
          className="flex items-center justify-between pt-2"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="flex items-center space-x-2 text-xs" style={{ color: '#C8DDE6' }}>
            <Icon name="UserCircleIcon" size={16} style={{ color: '#6FD3E0' }} />
            <span>{article.author}</span>
          </div>
          <div className="flex items-center space-x-2 text-xs" style={{ color: '#C8DDE6' }}>
            <Icon name="ClockIcon" size={16} style={{ color: '#6FD3E0' }} />
            <span>{article.readTime}</span>
          </div>
        </div>

        <div className="flex items-center space-x-2 text-xs" style={{ color: '#C8DDE6' }}>
          <Icon name="CalendarIcon" size={16} style={{ color: '#6FD3E0' }} />
          <span>{formatDate(article.date)}</span>
        </div>

        {/* Tertiary CTA — orange text link with arrow */}
        <Link
          href={`/blog/${article.slug}`}
          className="inline-flex items-center gap-2 text-sm font-semibold transition-colors duration-200 mt-2"
          style={{ color: '#FF8A3D' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#ffaa6e'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#FF8A3D'; }}
        >
          <span>Read Article →</span>
        </Link>
      </div>
    </article>
  );
};

export default BlogArticleCard;